import React, { useState } from 'react';
import { FaKey, FaSpinner, FaEnvelope, FaCopy, FaCheck } from 'react-icons/fa';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordButton = ({ 
  userId, 
  userName, 
  userEmail, 
  userRole,
  resetEndpoint, // 'admin', 'manager', or 'advisor'
  onSuccess,
  className = '',
  compact = false // New prop for icon-only mode
}) => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [sendEmail, setSendEmail] = useState(true);
  const [copied, setCopied] = useState(false);

  const getEndpoint = () => {
    switch (resetEndpoint) {
      case 'admin':
        return `${API}/api/admin/reset-password/${userId}`;
      case 'manager':
        return `${API}/api/manager/reset-password/${userId}`;
      case 'advisor':
        return `${API}/api/advisor/reset-client-password/${userId}`;
      default:
        return `${API}/api/admin/reset-password/${userId}`;
    }
  };

  const handleReset = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        getEndpoint(),
        { send_email: sendEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setResult(response.data);
      onSuccess && onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const copyPassword = () => {
    if (result?.temp_password) {
      navigator.clipboard.writeText(result.temp_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setError('');
    setResult(null);
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className={compact 
          ? `inline-flex items-center justify-center ${className}`
          : `inline-flex items-center px-3 py-1.5 bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 rounded text-sm transition-colors ${className}`
        }
        title="Reset Password"
        data-testid={`reset-password-btn-${userId}`}
      >
        <FaKey className={compact ? "" : "mr-1"} />
        {!compact && "Reset Password"}
      </button>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 w-full max-w-md border border-[#2D5A8F] shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Reset Password</h3>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>

            {/* User Info */}
            <div className="bg-[#0A1929] rounded-lg p-4 mb-4">
              <p className="text-white font-medium">{userName}</p>
              <p className="text-gray-400 text-sm">{userEmail}</p>
              <span className={`inline-block mt-2 px-2 py-0.5 rounded text-xs font-medium ${
                userRole === 'advisor' ? 'bg-blue-500/20 text-blue-400' :
                userRole === 'manager' ? 'bg-purple-500/20 text-purple-400' :
                'bg-green-500/20 text-green-400'
              }`}>
                {userRole}
              </span>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-200 text-sm">
                {error}
              </div>
            )}

            {/* Result */}
            {result ? (
              <div className="space-y-4">
                <div className="p-4 bg-green-500/20 border border-green-500 rounded-lg">
                  <p className="text-green-400 font-medium mb-2">Password Reset Successful!</p>
                  
                  {result.email_sent ? (
                    <div className="flex items-center text-green-200 text-sm">
                      <FaEnvelope className="mr-2" />
                      New password sent to {result.user_email}
                    </div>
                  ) : result.temp_password ? (
                    <div>
                      <p className="text-gray-300 text-sm mb-2">New temporary password:</p>
                      <div className="flex items-center bg-[#0A1929] rounded p-3">
                        <span className="font-mono text-yellow-400 flex-1" data-testid="new-temp-password">
                          {result.temp_password}
                        </span>
                        <button
                          onClick={copyPassword}
                          className="ml-2 p-2 hover:bg-white/10 rounded transition-colors"
                          title="Copy password"
                        >
                          {copied ? (
                            <FaCheck className="text-green-400" />
                          ) : (
                            <FaCopy className="text-gray-400" />
                          )}
                        </button>
                      </div>
                      <p className="text-orange-400 text-xs mt-2">
                        Please share this password securely with the user.
                      </p>
                    </div>
                  ) : null}
                </div>
                
                <button
                  onClick={handleClose}
                  className="w-full py-2 bg-[#2D5A8F] hover:bg-[#3D6A9F] text-white rounded transition-colors"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                {/* Email Option */}
                <div className="mb-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-500 text-yellow-500 focus:ring-yellow-500 mr-3"
                    />
                    <span className="text-gray-300 text-sm">
                      Send new password via email
                    </span>
                  </label>
                  {!sendEmail && (
                    <p className="text-orange-400 text-xs mt-2 ml-7">
                      Password will be shown on screen for you to share manually.
                    </p>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-3">
                  <button
                    onClick={handleClose}
                    className="flex-1 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReset}
                    disabled={loading}
                    className="flex-1 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded transition-colors flex items-center justify-center"
                    data-testid="confirm-reset-btn"
                  >
                    {loading ? (
                      <>
                        <FaSpinner className="animate-spin mr-2" />
                        Resetting...
                      </>
                    ) : (
                      <>
                        <FaKey className="mr-2" />
                        Reset Password
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ResetPasswordButton;
